/**
 * CanvasBottomPalette — the floating tool palette pinned to the bottom
 * centre of the canvas.
 *
 * Source of truth: Figma "Canvas UI 2.0" → frame "Default Canvas (Node base)"
 * (node 355:6138) → "Navbar" (node 355:6242). That frame is authored at 2x
 * (3840x2160), so every measurement below is the halved 1x value — a 62.5px
 * Figma icon slot is 31.25px here.
 *
 * Layout, left to right:
 *
 *   [+] [Hand] [Library] [CloudDownload] [Keyboard] | [Network|PanelLeftOpen]
 *
 * The add-node slot leads, drawn as the filled circle the design calls for.
 * The tool slot is a picker: the icon shows the active pointer tool and the
 * caret beside it (Figma vector 355:6247) opens a menu to switch. It renders
 * only when the parent passes `onToolChange`; the list view, which reuses
 * this palette and has no canvas to drag, omits it. After the
 * divider sits the canvas-vs-list <WorkspaceViewSwitch>; it renders only when
 * the parent passes `viewMode`, and is the canvas's only copy of that switch.
 *
 * Deliberately presentational about APP state: it owns no drawer or modal
 * state, and the active tool is the parent's. The tool menu's open/closed
 * flag IS local — it is transient popover chrome that nothing outside the
 * palette can observe, so lifting it would be prop-drilling for its own sake.
 */

import { memo, useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Check,
  CloudDownload,
  Hand,
  Keyboard,
  Library,
  MousePointer2,
  Plus,
} from 'lucide-react'
import { FlyoutPortal } from './nodes/shared/FlyoutPortal'
import {
  WorkspaceViewSwitch,
  type WorkspaceViewMode,
} from '../workspace/WorkspaceViewSwitch'
import { CANVAS_TOOLS, type CanvasTool } from './canvasTool'
import { usePaletteAnalytics, type PaletteSurfaceControl } from '../../analytics/paletteAnalytics'
import type { PaletteSurface } from '../../analytics/generated/analyticsEvents'
import { cn } from '../../lib/utils'

/** Icon per pointer tool, so the tool slot's glyph tracks the active tool. */
const TOOL_ICON: Record<CanvasTool, typeof Hand> = {
  hand: Hand,
  select: MousePointer2,
}

export interface CanvasBottomPaletteProps {
  /** Slot 1 — add a node to the canvas. */
  onOpenAddNode?: () => void
  isAddNodeOpen?: boolean
  /** Slot 2 — pointer tool picker. Omitted `onToolChange` → not rendered (list view). */
  activeTool?: CanvasTool
  onToolChange?: (tool: CanvasTool) => void
  /** Slot 3 — asset library. */
  onOpenAssetManager?: () => void
  isAssetManagerOpen?: boolean
  /** Slot 4 — download generated assets. */
  onOpenDownloadAssets?: () => void
  isDownloadAssetsOpen?: boolean
  /** Slot 5 — keyboard shortcut cheatsheet. */
  onOpenKeyboardShortcuts?: () => void
  isKeyboardShortcutsOpen?: boolean
  /** After the divider — canvas/list view switch. Omitted → not rendered. */
  viewMode?: WorkspaceViewMode
  onViewModeChange?: (mode: WorkspaceViewMode) => void
  /**
   * Which toolbar this instance is, for analytics attribution (RFC-1577): the
   * canvas renders `canvas_palette`, list view reuses the palette as
   * `list_palette`.
   */
  analyticsSurface?: PaletteSurface
}

/**
 * One 31.25px icon slot. `active` paints the #3D4143 fill the Figma frame
 * uses for the selected hand tool.
 */
function PaletteButton({
  label,
  active = false,
  disabled = false,
  hasPopup = false,
  expanded,
  onClick,
  testId,
  children,
}: {
  label: string
  active?: boolean
  disabled?: boolean
  /** Renders as a listbox trigger instead of a toggle button. */
  hasPopup?: boolean
  expanded?: boolean
  onClick?: () => void
  testId: string
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      // A popup trigger is not a toggle: announcing `aria-pressed` alongside
      // `aria-haspopup` tells a screen reader the control is both, which is
      // contradictory.
      aria-pressed={hasPopup ? undefined : active}
      aria-haspopup={hasPopup ? 'listbox' : undefined}
      aria-expanded={hasPopup ? expanded : undefined}
      title={label}
      data-testid={testId}
      className={cn(
        'flex h-[31px] w-[31px] shrink-0 items-center justify-center rounded-lg transition-colors',
        'text-chrome/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        active ? 'bg-[#3D4143] text-chrome' : 'hover:bg-white/10 hover:text-chrome',
        disabled && 'cursor-not-allowed opacity-40 hover:bg-transparent',
      )}
    >
      {children}
    </button>
  )
}

export const CanvasBottomPalette = memo(function CanvasBottomPalette({
  activeTool = 'hand',
  onToolChange,
  onOpenAssetManager,
  isAssetManagerOpen = false,
  onOpenAddNode,
  isAddNodeOpen = false,
  onOpenDownloadAssets,
  isDownloadAssetsOpen = false,
  onOpenKeyboardShortcuts,
  isKeyboardShortcutsOpen = false,
  viewMode,
  onViewModeChange,
  analyticsSurface = 'canvas_palette',
}: CanvasBottomPaletteProps) {
  const { t } = useTranslation('canvas')
  const [isToolMenuOpen, setIsToolMenuOpen] = useState(false)
  const toolSlotRef = useRef<HTMLDivElement>(null)
  const ActiveToolIcon = TOOL_ICON[activeTool]
  const analytics = usePaletteAnalytics()

  // A click that OPENS a surface starts an interaction; a click that toggles it
  // closed is recorded by the provider's open-flag watcher, like any other close.
  const openingClick = (control: PaletteSurfaceControl, isOpen: boolean, onOpen?: () => void) => () => {
    if (!isOpen) analytics?.open(analyticsSurface, control)
    onOpen?.()
  }

  // The tool menu's open flag is local, so its close is recorded here.
  useEffect(() => {
    if (!isToolMenuOpen) analytics?.closed('tool')
  }, [isToolMenuOpen, analytics])

  const toggleToolMenu = () => {
    if (!isToolMenuOpen) analytics?.open(analyticsSurface, 'tool')
    setIsToolMenuOpen((open) => !open)
  }

  const pickTool = (tool: CanvasTool) => {
    analytics?.step('tool', 'pick', { option: tool })
    setIsToolMenuOpen(false)
    onToolChange?.(tool)
  }

  const changeViewMode = (mode: WorkspaceViewMode) => {
    analytics?.instant(analyticsSurface, 'view_switch', 'pick', { option: mode })
    onViewModeChange?.(mode)
  }

  return (
    <div
      role="toolbar"
      aria-label={t('canvasPalette.label')}
      data-testid="canvas-bottom-palette"
      className={cn(
        'flex h-[50px] items-center gap-[37.5px] rounded-[18px] px-[30px]',
        'border border-white/15 bg-white/[0.08] backdrop-blur-[10px]',
        'shadow-[0_8px_10px_rgba(0,0,0,0.15)]',
      )}
    >
      {/* Slot 1 — add node. A filled circle rather than a PaletteButton
          glyph, so the primary create action reads first. */}
      <button
        type="button"
        onClick={openingClick('add_node', isAddNodeOpen, onOpenAddNode)}
        aria-label={t('canvasPalette.addNodes')}
        aria-pressed={isAddNodeOpen}
        title={t('canvasPalette.addNodes')}
        data-testid="canvas-palette-add-node"
        className={cn(
          'flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-full transition-colors',
          'bg-chrome/85 text-chrome-foreground/80 hover:bg-chrome',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
          isAddNodeOpen && 'bg-chrome ring-2 ring-white/40',
        )}
      >
        <Plus className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
      </button>

      {onToolChange && (
        <>
          {/* Slot 2 — pointer tool picker. Icon and caret both open the menu;
              with only two tools, a separate "activate the already-active tool"
              click target would do nothing. */}
          <div ref={toolSlotRef} className="flex shrink-0 items-center gap-[3px]">
            <PaletteButton
              label={t(`canvasPalette.tool.${activeTool}`)}
              active
              hasPopup
              expanded={isToolMenuOpen}
              onClick={toggleToolMenu}
              testId="canvas-palette-tool"
            >
              <ActiveToolIcon className="h-[22px] w-[22px]" aria-hidden="true" />
            </PaletteButton>
            <button
              type="button"
              onClick={toggleToolMenu}
              aria-label={t('canvasPalette.toolMenu')}
              aria-haspopup="listbox"
              aria-expanded={isToolMenuOpen}
              title={t('canvasPalette.toolMenu')}
              data-testid="canvas-palette-tool-menu"
              className={cn(
                'flex h-[31px] w-[10px] shrink-0 items-center justify-center rounded-sm',
                'text-chrome/75 transition-colors hover:text-chrome',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              )}
            >
              <svg
                width="7"
                height="4"
                viewBox="0 0 7 4"
                fill="currentColor"
                aria-hidden="true"
                className={cn('transition-transform', isToolMenuOpen && 'rotate-180')}
              >
                <path d="M0 0h7L3.5 4z" />
              </svg>
            </button>
          </div>

          <FlyoutPortal
            triggerRef={toolSlotRef}
            isOpen={isToolMenuOpen}
            onClose={() => setIsToolMenuOpen(false)}
            onEscape={() => setIsToolMenuOpen(false)}
            minWidth={168}
            testId="canvas-palette-tool-flyout"
            surfaceClassName="rounded-lg border border-border bg-surface-1 py-1 shadow-xl"
          >
            <div role="listbox" aria-label={t('canvasPalette.toolMenu')}>
              {CANVAS_TOOLS.map((tool) => {
                const ToolIcon = TOOL_ICON[tool]
                const selected = tool === activeTool
                return (
                  <button
                    key={tool}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onClick={() => pickTool(tool)}
                    data-testid={`canvas-palette-tool-option-${tool}`}
                    className={cn(
                      'flex w-full items-center gap-2.5 whitespace-nowrap px-3 py-1.5 text-left text-xs font-medium',
                      'transition-colors hover:bg-surface-2 hover:text-foreground',
                      selected ? 'text-foreground' : 'text-text-secondary',
                    )}
                  >
                    <ToolIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                    <span className="flex-1">{t(`canvasPalette.tool.${tool}`)}</span>
                    <Check
                      aria-hidden="true"
                      className={cn('h-3.5 w-3.5 shrink-0', !selected && 'invisible')}
                    />
                  </button>
                )
              })}
            </div>
          </FlyoutPortal>
        </>
      )}

      {/* Slot 3 — asset library */}
      <PaletteButton
        label={t('canvasPalette.assetManager')}
        active={isAssetManagerOpen}
        onClick={openingClick('asset_library', isAssetManagerOpen, onOpenAssetManager)}
        testId="canvas-palette-asset-manager"
      >
        <Library className="h-[22px] w-[22px]" aria-hidden="true" />
      </PaletteButton>

      {/* Slot 4 — download assets */}
      <PaletteButton
        label={t('canvasPalette.downloadAssets')}
        active={isDownloadAssetsOpen}
        onClick={openingClick('download', isDownloadAssetsOpen, onOpenDownloadAssets)}
        testId="canvas-palette-download-assets"
      >
        <CloudDownload className="h-[22px] w-[22px]" aria-hidden="true" />
      </PaletteButton>

      {/* Slot 5 — keyboard shortcuts */}
      <PaletteButton
        label={t('canvasPalette.keyboardShortcuts')}
        active={isKeyboardShortcutsOpen}
        onClick={openingClick('shortcuts', isKeyboardShortcutsOpen, onOpenKeyboardShortcuts)}
        testId="canvas-palette-keyboard-shortcuts"
      >
        <Keyboard className="h-[22px] w-[22px]" aria-hidden="true" />
      </PaletteButton>

      {viewMode && onViewModeChange && (
        <>
          <div
            aria-hidden="true"
            data-testid="canvas-palette-divider"
            className="h-[30px] w-px shrink-0 bg-white/15"
          />
          <WorkspaceViewSwitch value={viewMode} onChange={changeViewMode} variant="palette" />
        </>
      )}
    </div>
  )
})
